import * as _ from 'lodash'
import { Observable, of } from 'rxjs'
import { first, map, mergeMap, switchMap, tap } from 'rxjs/operators'

import { Work, Expression, Manifestation, Item, Wemi } from '@skinsoft/types'
import { expressionSelectors, itemSelectors, manifestationSelectors } from '@skinsoft/selectors'
import { API_ENDPOINTS } from '@skinsoft/network'
import { 
    WorkDataStore,
    ExpressionDataStore,
    ManifestationDataStore,
    ItemDataStore,
} from '@skinsoft/entity'

export class WemiUtils {
    public static async mergeWemiWithNewItem(wemi: Wemi, item: Item): Promise<Wemi> {
        wemi.item = ItemDataStore.getInstance().recursiveGenericMerge(wemi.item, item)
        wemi.manifestation = ManifestationDataStore.getInstance().recursiveGenericMerge(
            wemi.manifestation,
            await ManifestationDataStore.getInstance().fetchOne(_.get(wemi, 'item.linkToManifestation.referentManifestation')).toPromise()
        )
        wemi.expression = ExpressionDataStore.getInstance().recursiveGenericMerge(
            wemi.expression,
            await ExpressionDataStore.getInstance().fetchOne(_.get(wemi, 'manifestation.linkToExpression.referentExpression')).toPromise()
        )
        wemi.work = WorkDataStore.getInstance().recursiveGenericMerge(
            wemi.work, 
            await WorkDataStore.getInstance().fetchOne(_.get(wemi, 'expression.expressionSchema.referentWork')).toPromise()
        )
        return wemi
    }

    /**
     * 
     * @param itemId Builds a full Wemi object from the item id
     */
    public static getWemiFromItem(itemId: string): Observable<Wemi> {
        return ItemDataStore.getInstance().fetchOne(itemId)
            .pipe(
                map(item => ({ item })),
                switchMap(
                    wemi => {
                        if (wemi.item) {
                            const manifestationId = itemSelectors.manifestation(wemi.item);
                            if (manifestationId) {
                                return ManifestationDataStore.getInstance().fetchOne(manifestationId).pipe(
                                    map(manifestation => ({ ...wemi, manifestation }))
                                )
                            }
                        }
                        return of({ ...wemi, manifestation: null })
                    }
                ),
                switchMap(
                    wemi => {
                        if (wemi.manifestation) {
                            const expressionId = manifestationSelectors.expression(wemi.manifestation);
                            if (expressionId) {
                                return ExpressionDataStore.getInstance().fetchOne(expressionId).pipe(
                                    map(expression => ({ ...wemi, expression }))
                                )
                            }
                        }
                        return of({ ...wemi, expression: null })
                    }
                ),
                mergeMap(
                    wemi => {
                        if (wemi.expression) {
                            const workId = expressionSelectors.work(wemi.expression);
                            if (workId) {
                                return WorkDataStore.getInstance().fetchOne(workId).pipe(
                                    map(work => ({ ...wemi, work }) as Wemi))
                            }
                        }
                        return of({ ...wemi, work: null } as Wemi)
                    }
                )
            )
    }

    public static getWemiFromItemOffline(itemId: string): Observable<Wemi> {
        return ItemDataStore.getInstance().fetchOneOfflineMode(itemId)
            .pipe(
                map(item => ({ item })),
                switchMap(
                    wemi => {
                        let manifestation: Manifestation | null = null;
                        if (wemi.item) {
                            const manifestationId = itemSelectors.manifestation(wemi.item);
                            if (manifestationId) {
                                return ManifestationDataStore.getInstance().fetchOneOfflineMode(manifestationId).pipe(
                                    map(fetchedManifestation => ({ ...wemi, manifestation: fetchedManifestation }))
                                )
                            }
                            return ManifestationDataStore.getInstance().createOffline({}).pipe(
                                tap(manifestation => {
                                    _.set(wemi.item, 'linkToManifestation.referentManifestation', manifestation.id)
                                    ItemDataStore.getInstance().update(wemi.item.id, wemi.item)
                                }),
                                map(manifestation => ({...wemi, manifestation}))
                            )
                        }
                        return of({ ...wemi, manifestation: null })
                    }
                ),
                switchMap(
                    wemi => {
                        let expression: Expression | null = null
                        if (wemi.manifestation) {
                            const expressionId = manifestationSelectors.expression(wemi.manifestation);
                            if (expressionId) {
                                return ExpressionDataStore.getInstance().fetchOneOfflineMode(expressionId).pipe(
                                    map(fetchedExpression => ({ ...wemi, expression: fetchedExpression }))
                                )
                            }
                            return ExpressionDataStore.getInstance().createOffline({}).pipe(
                                tap((expression) => _.set(wemi.manifestation, 'linkToExpression.referentExpression', expression.id)),
                                map(expression => ({ ...wemi, expression: expression}))
                            )
                        }
                        return of({ ...wemi, expression: null })
                    }
                ),
                switchMap(
                    wemi => {
                        let work: Work | null = null
                        if (wemi.expression) {
                            const workId = expressionSelectors.work(wemi.expression);
                            if (workId) {
                                return WorkDataStore.getInstance().fetchOneOfflineMode(workId).pipe(
                                    map(fetchedWork => ({ ...wemi, work: fetchedWork }) as Wemi))
                            }
                        }
                        return  WorkDataStore.getInstance().createOffline({}).pipe(
                            tap(work => _.set(wemi.expression as any, 'expressionSchema.referentWork', work.id)),
                            map(work => ({ ...wemi, work}))
                        )
                    }
                )
            )
    }

    public static removeEmptyObjectsFromWemi(wemi: Wemi) {
        const fixSubelement = ((item: { [key: string]: any | any[] }) => {
            for (const [key, value] of Object.entries(item)) {
                if (typeof value === 'object' && _.isEmpty(value)) {
                    _.set(item, key, null)
                } else if (Array.isArray(value)) {
                    value.filter(_.identity).forEach(e => fixSubelement(e))
                    const nonEmptyValues = value.filter(o => !(typeof o === 'object' && o && _.isEmpty(o)))
                    _.set(item, key, nonEmptyValues.length === 0 ? null : nonEmptyValues)
                } else if (typeof value === 'object' && value) {
                    fixSubelement(value)
                    if (Object.values(value).every(e => !e)) {
                        _.set(item, key, null)
                    }
                }
            }
        })

        fixSubelement(wemi)
    }

    public static async saveWemi(wemi: Wemi, ignoreCachedStatus: boolean = false): Promise<any> {
        let entityTypeItem = wemi?.item?.type ?? wemi?.item?.entityType
        if(entityTypeItem) {
            entityTypeItem = _.lowerFirst(entityTypeItem)
        }
        const endpointItem = API_ENDPOINTS[entityTypeItem as string]

        let entityTypeWork = wemi?.work?.type ?? wemi?.work?.entityType
        if(entityTypeWork) {
            entityTypeWork = _.lowerFirst(entityTypeWork)
        }
        const endpointWork = API_ENDPOINTS[entityTypeWork as string]

        let entityTypeExpression = wemi?.expression?.type ?? wemi?.expression?.entityType
        if(entityTypeExpression) {
            entityTypeExpression = _.lowerFirst(entityTypeExpression)
        }
        const endpointExpression = API_ENDPOINTS[entityTypeExpression as string]

        let entityTypeManifestation = wemi?.manifestation?.type ?? wemi?.manifestation?.entityType
        if(entityTypeManifestation) {
            entityTypeManifestation = _.lowerFirst(entityTypeManifestation)
        }
        const endpointManifestation = API_ENDPOINTS[entityTypeManifestation as string]


        return Promise.all(
            [
                // Save Work 
                wemi.work ? WorkDataStore.getInstance()
                    .update(wemi.work.id, wemi.work, endpointWork, ignoreCachedStatus)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Expression
                wemi.expression ? ExpressionDataStore.getInstance()
                    .update(wemi.expression.id, wemi.expression, endpointExpression, ignoreCachedStatus)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Manifestation
                wemi.manifestation ? ManifestationDataStore.getInstance()
                    .update(wemi.manifestation.id, wemi.manifestation, endpointManifestation, ignoreCachedStatus)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Item
                wemi.item ? ItemDataStore.getInstance()
                    .update(wemi.item.id, wemi.item, endpointItem, ignoreCachedStatus)
                    .pipe(
                        first()
                    ).toPromise() : null
            ]
        )
    }

    public static async saveWemiOffline(wemi: Wemi): Promise<any> {
        return Promise.all(
            [
                // Save Work 
                wemi.work ? WorkDataStore.getInstance()
                    .updateOfflineMode(wemi.work.id, wemi.work)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Expression
                wemi.expression ? ExpressionDataStore.getInstance()
                    .updateOfflineMode(wemi.expression.id, wemi.expression)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Manifestation
                wemi.manifestation ? ManifestationDataStore.getInstance()
                    .updateOfflineMode(wemi.manifestation.id, wemi.manifestation)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Save Item
                wemi.item ? ItemDataStore.getInstance()
                    .updateOfflineMode(wemi.item.id, wemi.item)
                    .pipe(
                        first()
                    ).toPromise() : null
            ]
        )
    }

    public static async deleteWemi(wemi: Wemi): Promise<any> {
        return Promise.all(
            [
                // Delete Work 
                wemi.work ? WorkDataStore.getInstance()
                    .delete(wemi.work.id)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Delete Expression
                wemi.expression ? ExpressionDataStore.getInstance()
                    .delete(wemi.expression.id)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Delete Manifestation
                wemi.manifestation ? ManifestationDataStore.getInstance()
                    .delete(wemi.manifestation.id)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Delete Item
                wemi.item ? ItemDataStore.getInstance()
                    .delete(wemi.item.id)
                    .pipe(
                        first()
                    ).toPromise() : null
            ]
        )
    }

    public static async deleteModificationsWemi(wemi: Wemi): Promise<any> {
        return Promise.all(
            [
                // Delete modifications Work 
                wemi.work ? WorkDataStore.getInstance()
                    .deleteModifications(wemi.work.id)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Delete modifications Expression
                wemi.expression ? ExpressionDataStore.getInstance()
                    .deleteModifications(wemi.expression.id)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Delete modifications Manifestation
                wemi.manifestation ? ManifestationDataStore.getInstance()
                    .deleteModifications(wemi.manifestation.id)
                    .pipe(
                        first()
                    )
                    .toPromise() : null,

                // Delete modifications Item
                wemi.item ? ItemDataStore.getInstance()
                    .deleteModifications(wemi.item.id)
                    .pipe(
                        first()
                    ).toPromise() : null
            ]
        )
    }

}

export function isWemi(wemi: any) {
    return "item" in wemi && "manifestation" in wemi && "expression" in wemi && "work" in wemi;
}